// Formatters de fecha centralizados.
// Formato display de la app: YYYY-MM-DD (ISO 8601 corto).
//
// Fuente única para evitar duplicados (ver historial: existían 17+ formatDate
// inline con comportamientos distintos, lo que producía fechas inconsistentes
// en distintas pantallas).

const DEFAULT_FALLBACK = "—";

const toDate = (value) => {
  if (value instanceof Date) return value;
  if (value === null || value === undefined || value === "") return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Formatea una fecha en formato YYYY-MM-DD (ISO 8601 corto).
 *
 * @param {string|Date|null|undefined} value
 * @param {*} [fallback="—"] - Valor a devolver si la fecha es inválida o vacía.
 * @returns {string|*} YYYY-MM-DD o el fallback (puede ser cualquier tipo).
 *
 * Ejemplos:
 *   formatDate("2026-04-28T00:00:00")   → "2026-04-28"
 *   formatDate(null)                    → "—"
 *   formatDate("", "N/A")               → "N/A"
 *   formatDate("invalid", null)         → null
 */
export const formatDate = (value, fallback = DEFAULT_FALLBACK) => {
  const d = toDate(value);
  if (!d) return fallback;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/**
 * Formatea fecha + hora: YYYY-MM-DD hh:mm AM/PM.
 *
 * @param {string|Date|null|undefined} value
 * @param {*} [fallback="—"]
 * @returns {string|*}
 *
 * Ejemplo:
 *   formatDateTime("2026-04-28T14:30:00") → "2026-04-28 02:30 PM"
 */
export const formatDateTime = (value, fallback = DEFAULT_FALLBACK) => {
  const d = toDate(value);
  if (!d) return fallback;
  const datePart = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const hours24 = d.getHours();
  const ampm = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 || 12;
  const timePart = `${pad2(hours12)}:${pad2(d.getMinutes())} ${ampm}`;
  return `${datePart} ${timePart}`;
};

/**
 * Formatea fecha en formato largo con nombre de mes (US locale).
 *
 * @param {string|Date|null|undefined} value
 * @param {*} [fallback="—"]
 * @returns {string|*}
 *
 * Ejemplo:
 *   formatDateLong("2026-04-28") → "April 28, 2026"
 */
export const formatDateLong = (value, fallback = DEFAULT_FALLBACK) => {
  const d = toDate(value);
  if (!d) return fallback;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * Formato ISO corto (YYYY-MM-DD) para HTML <input type="date"> y para
 * payloads enviados al backend. NO usar para mostrar al usuario.
 *
 * @param {string|Date|null|undefined} value
 * @returns {string} YYYY-MM-DD o cadena vacía si la fecha es inválida.
 */
export const formatDateForInput = (value) => {
  const d = toDate(value);
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
